/**
 * One registration per person, across every tab.
 *
 * Two tabs of the console both logging into mod_verto with the same endpoint is not a cosmetic
 * problem: FreeSWITCH sends the ring to whichever session it decides on, so a rep with the
 * console open twice silently loses half their inbound calls. Verto also has no concept of a
 * second registration for the same user winning cleanly — the loser just sits there looking
 * connected.
 *
 * So exactly one tab holds the line. The lock is a `localStorage` key with a timestamp the
 * holder refreshes; other tabs watch the `storage` event and go to standby. A stale heartbeat
 * (a crashed tab) is claimable after `STALE_AFTER_MS`, so a browser crash does not leave a rep
 * unable to take calls until they clear site data.
 *
 * `localStorage` rather than the Web Locks API deliberately: Web Locks releases the moment the
 * holding tab is discarded, and we want a short grace period so a reload does not drop the
 * registration and re-register a second later.
 *
 * ## Two identities, and why
 *
 * A record carries both a **tabId** (sticky for the life of the tab, kept in `sessionStorage`)
 * and a **sessionId** (fresh on every page load). They answer two different questions:
 *
 *   *May I take the line?*  — yes if nobody holds it, if it went stale, **or if the holder is
 *     this same tab**. That last clause is the one that matters in practice. A page unload does
 *     not run React effect cleanups, so a reload always leaves behind a record whose heartbeat
 *     is at most two seconds old; with a freshly random id per page load the reloaded tab saw
 *     its own leftover record as "another tab" and sat in standby for seven seconds, showing
 *     "Use the phone here" with nothing else open. Persisting the tab id makes a reload
 *     recognise its own lock and reclaim it instantly.
 *
 *   *Do I still hold it?* — only if the record's sessionId is mine. Browsers COPY
 *     `sessionStorage` into a duplicated tab, so a duplicate inherits the tab id; without the
 *     per-load half, both pages would believe they held the line and both would register. With
 *     it, the duplicate takes over cleanly and the original drops to standby, which is the
 *     behaviour a user duplicating a tab would expect anyway.
 */

import { randomId, stickyTabValue } from "@/softphone/tab-storage";

const LOCK_KEY = "acl.softphone.lock";
const TAB_ID_KEY = "acl.softphone.tabId";
const HEARTBEAT_MS = 2000;
const STALE_AFTER_MS = 7000;

interface LockRecord {
  tabId: string;
  /** Identifies the page load that wrote this, so a reload or duplicate is distinguishable. */
  sessionId: string;
  at: number;
}

function read(): LockRecord | null {
  try {
    const raw = localStorage.getItem(LOCK_KEY);
    return raw ? (JSON.parse(raw) as LockRecord) : null;
  } catch {
    return null;
  }
}

function write(record: LockRecord | null): void {
  try {
    if (record === null) localStorage.removeItem(LOCK_KEY);
    else localStorage.setItem(LOCK_KEY, JSON.stringify(record));
  } catch {
    /* storage blocked: fall back to letting this tab hold the line */
  }
}

/** True when `current` is a record this page load may take over without asking the user. */
export function claimable(current: LockRecord | null, tabId: string, now: number): boolean {
  if (!current) return true;
  // Our own tab, from before a reload: reclaim it rather than queue behind ourselves.
  if (current.tabId === tabId) return true;
  return now - current.at > STALE_AFTER_MS;
}

export class SessionLock {
  /** Sticky for the life of the tab, so a reload is recognised as the same holder. */
  readonly tabId = stickyTabValue(TAB_ID_KEY, randomId);
  /** This page load. Ownership is checked against this, never against `tabId` alone. */
  readonly sessionId = randomId();
  private timer: ReturnType<typeof setInterval> | undefined;
  private onLost: (() => void) | undefined;
  private storageListener: ((e: StorageEvent) => void) | undefined;

  /** True when nobody holds the lock, the holder is this tab, or the holder went stale. */
  canAcquire(): boolean {
    return claimable(read(), this.tabId, Date.now());
  }

  held(): boolean {
    return read()?.sessionId === this.sessionId;
  }

  /** Take the line for this tab. `onLost` fires if another tab takes it over. */
  acquire(onLost: () => void): boolean {
    if (!this.canAcquire()) return false;
    this.onLost = onLost;
    this.write();

    this.timer = setInterval(() => {
      const current = read();
      if (current && current.sessionId !== this.sessionId) {
        // Someone else claimed it (they saw us as stale, pressed "Use here", or this tab was
        // duplicated and the copy took over).
        this.stop();
        this.onLost?.();
        return;
      }
      this.write();
    }, HEARTBEAT_MS);

    // `storage` fires only in *other* tabs, which is exactly the signal we want: it tells us
    // immediately that another tab took over, without waiting for the next heartbeat.
    this.storageListener = (e: StorageEvent) => {
      if (e.key !== LOCK_KEY || !e.newValue) return;
      try {
        if ((JSON.parse(e.newValue) as LockRecord).sessionId !== this.sessionId) {
          this.stop();
          this.onLost?.();
        }
      } catch {
        /* ignore malformed */
      }
    };
    window.addEventListener("storage", this.storageListener);
    return true;
  }

  private write(): void {
    write({ tabId: this.tabId, sessionId: this.sessionId, at: Date.now() });
  }

  /** Take the line from whichever tab currently holds it. Backs "Use here". */
  steal(onLost: () => void): void {
    write(null);
    this.acquire(onLost);
  }

  release(): void {
    if (this.held()) write(null);
    this.stop();
  }

  private stop(): void {
    clearInterval(this.timer);
    this.timer = undefined;
    if (this.storageListener) {
      window.removeEventListener("storage", this.storageListener);
      this.storageListener = undefined;
    }
  }
}
